import React from 'react';

import classNames from 'classnames';

import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Switch } from '@buildoutinc/blueprint-react/ui/Switch';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import { Button } from 'components/ui/button';

import type { PermissionRow } from './types';

// The row's answers, recomputed from what is on screen rather than taken from the serializer. Present
// whenever the page is showing state the browser owns — while editing, and after a save, when the row's
// own copy describes the state that was there before it.
export interface RowState {
  granted: boolean;
  custom: boolean;
  attribution: string | null;
  blockedBy: string[];
  // Why the switch is dead, when the reason is not a missing prerequisite: superseded, forced on, or a
  // lock from outside the graph entirely.
  note: string | null;
  detail: React.ReactNode | null;
  // Absent when the state is being displayed rather than edited, which is what leaves the row read-only.
  controls?: {
    locked: boolean;
    onChange: (value: boolean) => void;
    onReset: () => void;
  };
}

interface Props {
  row: PermissionRow;
  state?: RowState;
}

// A custom row departed from what the roles say. Which direction it departed in is the useful part:
// "the roles turn this on" reads very differently from "no role turns this on".
// Driven by `granted`, not `roleDefault`: the sentence's subject is the state shown beside it, so keying
// off the other field let a row read "turned on for this person" next to an Off chip.
const customExplanation = (granted: boolean): string => (
  granted
    ? 'Turned on for this person. No assigned role turns this on.'
    : 'Turned off for this person. Their assigned roles turn this on.'
);

export default function PermissionRowItem({ row, state }: Props) {
  const granted = state ? state.granted : row.granted;
  const custom = state ? state.custom : row.source === 'custom';
  const attribution = state ? state.attribution : row.attribution;
  const blockedBy = state ? state.blockedBy : row.blockedBy;
  const note = state ? state.note : row.lockReason;
  // No fallback to row.detail: office_admin is the only row that carries one, and the browser replaces
  // its office list with a picker — falling back would keep showing the offices it had before the toggle
  // was switched off.
  const detail = state ? state.detail : row.detail;
  const controls = state?.controls;

  return (
    <li className="d-flex align-items-start justify-content-between gap-3 py-3 border-bottom">
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-2">
          <span className={classNames('fw-semibold', { 'text-muted': !granted })}>{row.label}</span>
          {row.deprecated && (
            <Tooltip>
              <Tooltip.Trigger delay={0} render={<Badge variant="secondary">Retired</Badge>} />
              <Tooltip.Content>
                This permission is no longer offered. It is kept because this person already had it, and
                cannot be granted to anyone else.
              </Tooltip.Content>
            </Tooltip>
          )}
        </div>
        {row.description && <p className="mb-0 text-muted fs-small">{row.description}</p>}
        {detail && <div className="mb-0 fs-small">{detail}</div>}
        {attribution && <p className="mb-0 text-muted fs-small">{attribution}</p>}
        {blockedBy.length > 0 && (
          <p className="mb-0 fs-small text-warning-emphasis">
            {`Not in effect until ${blockedBy.join(' and ')} is turned on.`}
          </p>
        )}
        {custom && <p className="mb-0 text-muted fs-small">{customExplanation(granted)}</p>}
        {note && <p className="mb-0 text-muted fs-small">{note}</p>}
        {controls && custom && (
          <Button
            appearance="accent"
            data-testid={`reset-${row.key}`}
            disabled={controls.locked}
            size="sm"
            variant="ghost"
            onClick={controls.onReset}
          >
            Reset to role default
          </Button>
        )}
      </div>

      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <Badge variant={custom ? 'primary' : 'outline'}>{custom ? 'Custom' : 'Default'}</Badge>
        {controls ? (
          <Switch
            aria-label={row.label}
            checked={granted}
            disabled={controls.locked}
            onCheckedChange={controls.onChange}
          />
        ) : (
          <span className={classNames('fs-small', granted ? 'text-success' : 'text-muted')}>
            {granted ? 'On' : 'Off'}
          </span>
        )}
      </div>
    </li>
  );
}
